<template>
  <div id="app">
    <div style="z-index:3; position:absolute;top:0;left:0" v-if="isDev">
      <button @click="dumpPreset">Dump Preset</button>
      <button @click="panic">Panic</button>
      <button @click="canPlay = !canPlay">canPlay</button>
    </div>
    <template v-if="canPlay">
      <help
        style="z-index:2"
        @toggle="helpShown = !helpShown"
        @hide="helpShown = false"
        :show="helpShown"
      ></help>
      <div class="synth__container">
        <Synthesizer ref="synth" @ready="animStop = true" />
      </div>
    </template>
    <transition name="background-in" @afterEnter="canPlay = true" :duration="3000">
      <section class="background" v-if="loaded" :class="{ 'can-play': animStop }">
        <div class="stars stars-1 animated"></div>
        <div class="stars stars-2 animated"></div>
        <div class="sun animated"></div>
        <div class="horizon animated"></div>
        <div class="tree"></div>
      </section>
    </transition>
  </div>
</template>

<script>
import LoadingComponent from "@/components/LoadingComponent";
const Synthesizer = () => ({
  component: import("@/components/Synthesizer"),
  loading: LoadingComponent
});
const Help = () => import("@/components/help/Help");
export default {
  name: "App",
  components: { Synthesizer, Help, LoadingComponent },
  data() {
    return {
      loaded: false,
      canPlay: false,
      helpShown: false,
      animStop: false
    };
  },
  computed: {
    isDev() {
      return process.env.NODE_ENV !== "production";
    }
  },
  methods: {
    dumpPreset() {
      this.$refs.synth.dumpPreset();
    },
    async panic() {
      this.$refs.synth.panic();
    },
    toggleAnalyser() {
      this.$refs.synth.toggleAnalyser();
    }
  },
  mounted() {
    window.addEventListener("load", () => {
      this.loaded = true;
    });
  }
};
</script>

<style lang="scss">
@import "@/assets/scss/main.scss";
@keyframes infiniteSlide {
  0% {
    background-position: 100% 0%;
  }
  100% {
    background-position: 100% 100%;
  }
}
@keyframes animStar {
  from {
    transform: translateY(0px);
  }
  to {
    transform: translateY(-1000px);
  }
}
/*
  Create retrowave sun clip animation
  @param $parts: number of parts in animation loop [1-Inf]
  @param $gap: max gap size (%) (gap will decrease in size from bottom [100%] to $limit) [0-100]
  @param $limit: position (%) from top that stops fading effect [0-100]
  Note: some params combination will require a little fine tuning to get desired result
*/
/* prettier-ignore */
@mixin clipAnim($parts: 10, $gap: 10, $limit: 20) {
  $from: ();
  $to: ();
  $h: (100 - $limit) / $parts; //segment height
  $bottom: (100 + $h + $h + $gap) * 1%;

  @for $i from 1 through $parts {
    // from
    $gap1: $gap * (1 - $i / $parts ); // decreasing gap height

    $bottom: $bottom - $h - $gap1;
    $top: $bottom - $h;

    $x1: 0% $bottom;    // bottom left
    $x2: 100% $bottom;  // bottom right
    $x3: 100% $top;     // top right
    $x4: 0% $top;       // top left
    $from: append($from, #{$x1, $x2, $x3, $x4}, comma);

    // to
    $gap2: $gap * (1 - ($i + 1) / $parts);
    $btm: $top - $gap2;
    $top: $btm - $h;

    $x1: 0% $btm;
    $x2: 100% $btm;
    $x3: 100% $top;
    $x4: 0% $top;

    $to: append($to, #{$x1, $x2, $x3, $x4}, comma);
  }

  // top limit
  $x1: 0% ($limit * 1%);
  $x2: 100% ($limit * 1%);
  $x3: 100% 0%;
  $x4: 0% 0%;
  $from: append($from, #{$x1, $x2, $x3, $x4}, comma);
  $to: append($to, #{$x1, $x2, $x3, $x4}, comma);


  @keyframes clip {
    0% {
      clip-path: polygon(#{$from});
    }
    100% {
      clip-path: polygon(#{$to});
    }
  }
}

.background {
  $color: $primary;
  $secondary: adjust-hue($color, -62);
  $secondary: adjust-hue($color, -32);
  position: absolute;
  width: 100%;
  height: 100%;
  bottom: 0;
  left: 0;
  &::before {
    content: "";
    transition: opacity 0.5s ease-out;
    position: absolute;
    width: 100%;
    height: 100%;
    background-color: rgb(0, 6, 0);
    background: radial-gradient(ellipse at bottom, #510d5b 0%, transparent 50%),
      linear-gradient(to bottom, #00081b, #510d5b);
  }
  .animated {
    animation-play-state: running;
  }
  &.can-play {
    .animated {
      animation-play-state: paused;
    }
  }
  .horizon {
    background: inherit;
    height: 100%;
    width: 100%;
    left: -50%;
    right: 0;
    width: 200%;
    height: 100vh;
    transform: perspective(400px) rotateX(40deg) scale(2, 1) translate3d(0, 0, 0);

    transform-origin: bottom center;
    background-image: linear-gradient(to bottom, #330a46, #040304);
    box-shadow: 0 0px 40px 15px lighten($color, 1.4);
    z-index: 1;
    &::after {
      content: "";
      display: block;
      position: absolute;
      bottom: 0;
      right: 0;
      width: 100%;
      left: 0;
      height: 100%;
      padding: 1px;
      outline: 1px solid transparent;

      will-change: transform, background-position;
      -webkit-mask-image: -webkit-gradient(
        linear,
        left 90%,
        left top,
        from(rgba(0, 0, 0, 1)),
        to(rgba(0, 0, 0, 0))
      );
      mask-image: gradient(
        linear,
        left 90%,
        left top,
        from(rgba(0, 0, 0, 1)),
        to(rgba(0, 0, 0, 0))
      );
      // /* --------- */
      background-size: 10vmax 10vmax;
      -webkit-background-clip: content-box;
      -webkit-backface-visibility: hidden;
      background-clip: content-box;
      backface-visibility: hidden;
      /* ---------------------- */
      background-image: linear-gradient(to right, $color 3px, transparent 4px),
        linear-gradient(to bottom, $color 1px, transparent 4px);
      background-position: center bottom;
      transform-origin: bottom center;
      animation: infiniteSlide 15s linear infinite;
      animation-play-state: inherit;
    }
  }

  .sun {
    width: 30vmax;
    height: 30vmax;
    position: absolute;
    left: 50%;
    bottom: 25%;
    transform: translate(-50%, 0%);
    border-radius: 50%;
    $color: #f9bd04;
    $secondary: hsl(336, 100%, 50%);
    filter: drop-shadow(0px 5px 13px rgba($secondary, 0.75));
    @include clipAnim(10, 8, 15);
    &::after {
      content: "";
      position: absolute;
      width: 100%;
      height: 100%;
      border-radius: 50%;
      background-image: linear-gradient(to bottom, $color 0%, $secondary 60%);
      animation: clip 2s linear infinite;
      animation-play-state: inherit;
    }
  }

  .tree {
    width: 15vmax;
    height: 15vmax;
    position: absolute;
    left: 25%;
    bottom: 28%;
    filter: drop-shadow(-3px -2px 1px #e31576);
    &::after {
      content: "";
      width: 100%;
      height: 100%;
      display: block;
      background: #2d092d;
      mask-image: url("~@/assets/images/tree.png");
      mask-position: center bottom;
      mask-size: contain;
      mask-repeat: no-repeat;
    }
  }
}

.stars {
  width: 3px;
  height: 3px;
  background: transparent;

  &::after {
    content: " ";
    position: absolute;
    background: inherit;
    top: 1000px;
    width: 1px;
    height: 1px;
  }
  &-1 {
    animation: animStar 540s linear infinite;
    box-shadow: 1226px 1621px #fff, 1021px 1311px #fff, 589px 396px #fff, 106px 950px #fff,
      1628px 685px #fff, 1982px 1853px #fff, 683px 1196px #fff, 885px 147px #fff, 140px 572px #fff,
      965px 791px #fff, 1563px 422px #fff, 1427px 822px #fff, 1540px 599px #fff, 1246px 1681px #fff,
      1271px 1136px #fff, 1411px 641px #fff, 1108px 1981px #fff, 961px 1884px #fff, 788px 631px #fff,
      172px 783px #fff, 1729px 455px #fff, 1682px 1051px #fff, 911px 1455px #fff, 1652px 489px #fff,
      759px 1046px #fff, 1451px 1951px #fff, 101px 1610px #fff, 1469px 912px #fff,
      1214px 1292px #fff, 1368px 1018px #fff, 484px 470px #fff, 816px 322px #fff, 1821px 1326px #fff,
      327px 1936px #fff, 752px 550px #fff, 1215px 1924px #fff, 180px 122px #fff, 784px 1924px #fff,
      1597px 336px #fff, 248px 1429px #fff, 4px 181px #fff, 1801px 1613px #fff, 1581px 1719px #fff,
      640px 1391px #fff, 960px 302px #fff, 1483px 1207px #fff, 520px 1119px #fff, 25px 1754px #fff,
      545px 317px #fff, 1573px 751px #fff, 1415px 2px #fff, 101px 761px #fff, 679px 291px #fff,
      1785px 676px #fff, 1256px 136px #fff, 855px 197px #fff, 1399px 1973px #fff, 1243px 429px #fff,
      1281px 571px #fff, 839px 531px #fff, 789px 1980px #fff, 877px 783px #fff, 1526px 1850px #fff,
      1406px 428px #fff, 727px 1609px #fff, 1149px 1838px #fff, 78px 169px #fff, 1088px 284px #fff,
      670px 399px #fff, 711px 1923px #fff, 42px 1720px #fff, 667px 570px #fff, 1274px 693px #fff,
      1056px 644px #fff, 1937px 793px #fff, 1200px 602px #fff, 1493px 755px #fff, 545px 870px #fff,
      887px 270px #fff, 1290px 164px #fff, 265px 1185px #fff;
    &:after {
      width: 2px;
      height: 2px;
      box-shadow: 1226px 1621px #fff, 1021px 1311px #fff, 589px 396px #fff, 106px 950px #fff,
        1628px 685px #fff, 1982px 1853px #fff, 683px 1196px #fff, 885px 147px #fff, 140px 572px #fff,
        1414px 202px #fff, 1287px 375px #fff, 812px 233px #fff, 245px 366px #fff, 619px 1786px #fff,
        158px 727px #fff, 1051px 1759px #fff, 1232px 1572px #fff, 304px 515px #fff,
        1107px 1063px #fff, 1409px 293px #fff, 844px 1928px #fff, 1297px 766px #fff, 769px 53px #fff,
        1796px 1499px #fff, 1096px 1588px #fff, 6px 1066px #fff, 954px 1823px #fff, 449px 1318px,
        1288px 1530px #fff, 911px 1896px #fff, 689px 4px #fff, 806px 162px #fff, 1201px 604px #fff,
        908px 531px #fff, 137px 1123px #fff, 1165px 496px #fff, 1567px 1797px #fff, 614px 504px #fff,
        1551px 1165px #fff, 1309px 1032px #fff, 1321px 26px #fff, 1372px 326px #fff,
        1762px 1159px #fff, 728px 5px #fff, 1364px 459px #fff, 1333px 918px #fff, 1082px 1732px #fff,
        286px 319px #fff, 1636px 1220px #fff, 350px 966px #fff, 124px 219px #fff, 184px 1654px #fff,
        1195px 160px #fff, 1690px 737px #fff, 1386px 472px #fff, 1522px 1911px #fff,
        670px 1532px #fff, 498px 159px #fff, 540px 1997px #fff, 1792px 1327px #fff,
        880px 1023px #fff, 1917px 1938px #fff, 1859px 148px #fff, 1147px 1781px #fff,
        428px 1876px #fff, 1952px 605px #fff, 655px 80px #fff, 1500px 1545px #fff, 1656px 644px #fff,
        615px 247px #fff, 795px 1881px #fff, 1750px 586px #fff, 148px 1490px #fff, 1434px 74px #fff,
        417px 1969px #fff, 962px 1526px #fff, 835px 962px #fff, 330px 1767px #fff, 1557px 435px #fff,
        240px 341px #fff, 1857px 1589px #fff, 765px 281px #fff, 1452px 405px #fff,
        1480px 1724px #fff, 950px 1094px #fff, 710px 1388px #fff, 1333px 1919px #fff,
        706px 1589px #fff, 1476px 1465px #fff, 744px 291px #fff, 123px 870px #fff, 1406px 428px #fff,
        727px 1609px #fff, 1149px 1838px #fff, 78px 169px #fff, 1088px 284px #fff, 670px 399px #fff,
        711px 1923px #fff, 42px 1720px #fff, 667px 570px #fff, 1274px 693px #fff, 1056px 644px #fff,
        1937px 793px #fff, 1200px 602px #fff, 1493px 755px #fff, 545px 870px #fff, 887px 270px #fff,
        1290px 164px #fff, 265px 1185px #fff;
    }
  }
  &-2 {
    animation: animStar 360s linear infinite;
    box-shadow: 1948px 1304px #fff, 255px 1453px #fff, 146px 1809px #fff, 1216px 205px #fff,
      1418px 1507px #fff, 206px 1111px #fff, 734px 1490px #fff, 1985px 1787px #fff,
      188px 1154px #fff, 1447px 113px #fff, 1484px 1176px #fff, 1495px 1763px #fff,
      1597px 453px #fff, 1840px 1052px #fff, 655px 1587px #fff, 1171px 1430px #fff,
      1106px 410px #fff, 97px 1054px #fff, 714px 1158px #fff, 1877px 1980px #fff, 181px 105px #fff,
      1387px 1624px #fff, 485px 1406px #fff, 1770px 1868px #fff, 1403px 770px #fff,
      204px 1690px #fff, 300px 406px #fff, 103px 364px #fff, 1376px 774px #fff, 1885px 753px #fff,
      162px 822px #fff, 1944px 1848px #fff, 500px 269px #fff, 317px 1205px #fff, 49px 905px #fff,
      1897px 1698px #fff, 1937px 84px #fff, 1831px 1273px #fff, 1244px 1355px #fff, 731px 737px #fff,
      1536px 802px #fff, 628px 394px #fff, 1582px 1266px #fff, 1583px 668px #fff, 1548px 1611px #fff,
      303px 945px #fff, 1749px 980px #fff, 909px 732px #fff, 645px 808px #fff, 56px 1087px #fff,
      1320px 1359px #fff, 1053px 473px #fff, 1783px 834px #fff, 137px 1176px #fff, 786px 1701px #fff,
      656px 906px #fff, 620px 793px #fff, 1471px 423px #fff, 1046px 108px #fff, 1849px 1270px #fff,
      1152px 1323px #fff, 323px 623px #fff, 304px 1154px #fff, 678px 1427px #fff, 734px 955px #fff,
      1717px 674px #fff, 1974px 1431px #fff, 859px 1871px #fff, 822px 1386px #fff,
      1961px 1081px #fff, 750px 1690px #fff, 1387px 547px #fff, 776px 907px #fff, 435px 1934px #fff,
      542px 321px #fff, 320px 754px #fff, 622px 1759px #fff, 1758px 597px #fff, 742px 469px #fff,
      638px 1795px #fff, 1578px 1755px #fff, 1881px 1923px #fff, 148px 1001px #fff,
      1333px 1015px #fff, 663px 453px #fff, 166px 730px #fff, 319px 1423px #fff, 1571px 1401px #fff,
      1538px 1363px #fff, 851px 15px #fff, 1884px 590px #fff, 1016px 778px #fff, 1993px 1060px #fff,
      40px 444px #fff, 667px 639px #fff, 1299px 1074px #fff, 547px 923px #fff, 93px 1476px #fff,
      1411px 1712px #fff, 535px 1825px #fff;
    &::after {
      box-shadow: 1948px 1304px #fff, 255px 1453px #fff, 146px 1809px #fff, 1216px 205px #fff,
        1418px 1507px #fff, 206px 1111px #fff, 734px 1490px #fff, 1985px 1787px #fff,
        188px 1154px #fff, 1447px 113px #fff, 1484px 1176px #fff, 1495px 1763px #fff,
        1597px 453px #fff, 1840px 1052px #fff, 655px 1587px #fff, 1171px 1430px #fff,
        1106px 410px #fff, 97px 1054px #fff, 714px 1158px #fff, 1877px 1980px #fff, 181px 105px #fff,
        1387px 1624px #fff, 485px 1406px #fff, 1770px 1868px #fff, 1403px 770px #fff,
        204px 1690px #fff, 300px 406px #fff, 103px 364px #fff, 1376px 774px #fff, 1885px 753px #fff,
        162px 822px #fff, 1944px 1848px #fff, 500px 269px #fff, 317px 1205px #fff, 49px 905px #fff,
        1897px 1698px #fff, 1937px 84px #fff, 1831px 1273px #fff, 1244px 1355px #fff,
        731px 737px #fff, 1536px 802px #fff, 628px 394px #fff, 1582px 1266px #fff, 1583px 668px #fff,
        1548px 1611px #fff, 303px 945px #fff, 1749px 980px #fff, 909px 732px #fff, 645px 808px #fff,
        56px 1087px #fff, 1320px 1359px #fff, 1053px 473px #fff, 1783px 834px #fff,
        137px 1176px #fff, 786px 1701px #fff, 656px 906px #fff, 620px 793px #fff, 1471px 423px #fff,
        1046px 108px #fff, 1849px 1270px #fff, 1152px 1323px #fff, 323px 623px #fff,
        304px 1154px #fff, 678px 1427px #fff, 734px 955px #fff, 1717px 674px #fff,
        1974px 1431px #fff, 859px 1871px #fff, 822px 1386px #fff, 1961px 1081px #fff,
        750px 1690px #fff, 1387px 547px #fff, 776px 907px #fff, 435px 1934px #fff, 542px 321px #fff,
        320px 754px #fff, 622px 1759px #fff, 1758px 597px #fff, 742px 469px #fff, 638px 1795px #fff,
        1578px 1755px #fff, 1881px 1923px #fff, 148px 1001px #fff, 1333px 1015px #fff,
        663px 453px #fff, 166px 730px #fff, 319px 1423px #fff, 1571px 1401px #fff,
        1538px 1363px #fff, 851px 15px #fff, 1884px 590px #fff, 1016px 778px #fff,
        1993px 1060px #fff, 40px 444px #fff, 667px 639px #fff, 1299px 1074px #fff, 547px 923px #fff,
        93px 1476px #fff, 1411px 1712px #fff, 535px 1825px #fff;
    }
  }
}

.synth__container {
  display: flex;
  margin: auto;
  justify-content: center;
  align-items: center;
  z-index: 5;
}

.background-in {
  &-enter-active,
  &-leave-active {
    &::before {
      transition: opacity 1.5s ease-out;
    }
    .stars,
    .sun {
      transition-property: opacity, transform;
      transition-timing-function: ease-out;
    }
    .sun {
      transition-delay: 0.3s;
      transition-duration: 2s;
    }
    .stars {
      transition-duration: 0.2s;
      transition-delay: 1.4s;
    }
    .animated {
      animation-play-state: paused !important;
    }
  }
  &-enter {
    &::before,
    .stars {
      opacity: 0;
    }
    .sun {
      opacity: 0;
      transform: translate(-50%, 100%);
    }
  }
}
</style>
